<script setup lang="ts">
const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
  },
  currentPage: {
    type: Number,
    required: true,
  },
});
const emit = defineEmits<{
  (e: 'changePage', value: number): void;
}>();

const pagingSize = 10;
const pagingSizeHalf = pagingSize / 2;

const commonClasses = 'underline text-xl';

const isCurrentPageFirst = computed(() => {
  return props.currentPage === 1;
});

const isCurrentPageLast = computed(() => {
  return props.currentPage === props.totalPages;
});

function pageClasses(page: number) {
  const isCurrent = page === props.currentPage;

  return {
    [commonClasses]: true,
    'mx-2': true,
    'font-bold no-underline cursor-default': isCurrent,
  };
}

const visiblePages = computed(() => {
  // paginationが5ページしか表示されない
  const pages = [...Array(Math.min(props.totalPages, pagingSize))];

  // currentPageが最初かページ数がpagingSize以下のとき
  if (isInsidePageSize.value || props.currentPage <= pagingSize) {
    return pages.map((_, i) => i + 1);
  }

  // currentPageが最後の辺り
  if (props.currentPage > props.totalPages - pagingSize) {
    return pages.map((_, i) => props.totalPages - pagingSize + i + 1);
  }

  // currentPageが中央
  const firstPage = props.currentPage - pagingSizeHalf;
  return pages.map((_, i) => i + firstPage);
});

const isInsidePageSize = computed(() => {
  return props.totalPages <= pagingSize;
});

function changePage(pageNumber: number) {
  const normalizedPageNumber = Math.max(
    Math.min(pageNumber, props.totalPages),
    1,
  );

  // Eventは発火される必要ない判定
  if (normalizedPageNumber === props.currentPage) {
    return;
  }

  emit('changePage', normalizedPageNumber);
}
</script>

<template>
  <div v-if="totalPages >= 2" class="flex justify-center py-10 flex-wrap">
    <button
      v-if="!isCurrentPageFirst"
      :class="[commonClasses, 'mr-4']"
      @click="changePage(1)"
    >
      ...
    </button>
    <button
      v-if="!isCurrentPageFirst"
      :class="commonClasses"
      @click="changePage(currentPage - 1)"
    >
      &laquo;前へ
    </button>
    <div class="flex mx-2">
      <button
        v-for="(page, index) in visiblePages"
        :key="index"
        :class="pageClasses(page)"
        @click="changePage(page)"
      >
        {{ page }}
      </button>
    </div>
    <button
      v-if="!isCurrentPageLast"
      :class="commonClasses"
      @click="changePage(currentPage + 1)"
    >
      次へ&raquo;
    </button>
    <button
      v-if="!isCurrentPageLast"
      :class="[commonClasses, 'ml-4']"
      @click="changePage(totalPages)"
    >
      ...
    </button>
  </div>
</template>
